import type { MovementInfo } from '@az3fr/core';
import {
	Combobox,
	ComboboxButton,
	ComboboxInput,
	ComboboxOption,
	ComboboxOptions,
} from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useVirtualizer } from '@tanstack/react-virtual';
import Fuse from 'fuse.js';
import { CheckIcon, InfoIcon, UserIcon, XIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { TruncatedText } from '@/components/ui/TruncatedText';
import { equipmentSliceSelectors } from '@/entities/equipment/model/equipment.selector';
import { movementSliceSelectors } from '@/entities/movement/model/movement.selector';
import {
	createNewUserMovement,
	movementSliceActions,
} from '@/entities/movement/model/movement.slice';
import { useAppDispatch, useAppSelector } from '@/hooks/store.hook';
import { isTemporaryEntity, markAsTemporary } from '@/lib/temporary-entity';
import { cn } from '@/lib/utils';

const MOVEMENT_OPTION_HEIGHT = 36; // Height of each movement option in pixels
const MAX_VISIBLE_OPTIONS = 8; // Maximum number of options to show without scrolling

/**
 * Custom hook for fuzzy searching movements using Fuse.js
 * Organized with internal functions for better maintainability
 */
function useMovementCombobox(
	parentOnChange: (movement: MovementInfo | null) => void,
	initialQuery?: string,
) {
	const dispatch = useAppDispatch();
	const movements = useAppSelector(movementSliceSelectors.selectAll);
	const loadEquipments = useAppSelector(equipmentSliceSelectors.selectLoadEquipments);

	// Group related state together
	const [searchState, setSearchState] = useState<{
		query: string;
		selectedMovement: MovementInfo | null;
	}>({
		query: initialQuery ?? '',
		selectedMovement: null,
	});

	// Detect equipment keywords in query based on actual equipment names
	// Uses word boundary matching to avoid false positives (e.g., "press" in "cypress")
	const detectEquipmentInQuery = useCallback(
		(query: string): string[] => {
			if (!query || query.trim().length === 0) {
				return [];
			}
			const lowerQuery = query.toLowerCase();
			return loadEquipments
				.map((equipment) => equipment.name.toLowerCase())
				.filter((equipmentName) => {
					// Create regex with word boundaries: \b matches at word start/end
					// This ensures "press" matches "press" or "bench press" but not "cypress"
					const regex = new RegExp(`\\b${equipmentName}\\b`, 'i');
					return regex.test(lowerQuery);
				});
		},
		[loadEquipments],
	);

	// Search logic - handles fuzzy search and filtering
	const searchLogic = useMemo(() => {
		const fuse = new Fuse(movements, {
			keys: [
				{ name: 'name', weight: 0.7 },
				{ name: 'canonicalName', weight: 0.3 },
			],
			threshold: 0.4, // 0.0 = exact match, 1.0 = match anything
			includeScore: true,
			minMatchCharLength: 1,
			ignoreLocation: true, // Search anywhere in the string
		});

		const getSearchResults = (query: string) => {
			if (!query || query.trim().length === 0) {
				// Show all movements when no query (sorted by most recently updated)
				return {
					filteredMovements: movements.slice().sort((a, b) => (b.updatedAt > a.updatedAt ? 1 : -1)),
					hasExactMatch: false,
				};
			}

			// Use Fuse.js for fuzzy search and return results sorted by relevance score
			const results = fuse.search(query);
			// We return all the result items, not just top N, to allow full virtualization
			const filteredMovements = results.map((result) => result.item);

			// Only hide create button if there's an exact match (case-insensitive)
			const hasExactMatch =
				results.length > 0 && results[0]?.item?.name?.toLowerCase() === query.toLowerCase();

			return { filteredMovements, hasExactMatch };
		};

		return { getSearchResults };
	}, [movements]);

	// Creation logic - handles temporary movement and new movement creation
	const creationLogic = useMemo(() => {
		const temporaryMovement = markAsTemporary(
			createNewUserMovement({
				name: searchState.query,
				description: '', // Empty description for temporary movements
			}),
		);

		// @todo: Move toast to saga or thunk if this logic gets more complex
		// add error handling, etc.
		const createNewMovement = (name: string) => {
			const newMovement = createNewUserMovement({ name });
			dispatch(movementSliceActions.addedOne(newMovement));
			toast.success(`Movement "${newMovement.name}" created`);
			return newMovement;
		};

		return { temporaryMovement, createNewMovement };
	}, [searchState.query, dispatch]);

	// Selection logic - handles movement selection and state updates
	const selectionLogic = useMemo(() => {
		const setSelectedMovement = (selectedMovement: MovementInfo | null) => {
			setSearchState((prev) => ({ ...prev, selectedMovement }));
		};

		const setQuery = (query: string) => {
			setSearchState((prev) => ({ ...prev, query }));
		};

		const onChange = (value: MovementInfo | null) => {
			if (value === null) {
				setSelectedMovement(null);
				parentOnChange(null);
				return;
			}

			// Check if this is a temporary movement that needs to be created
			if (isTemporaryEntity(value)) {
				const newMovement = creationLogic.createNewMovement(searchState.query);
				setSelectedMovement(newMovement);
				parentOnChange(newMovement);
			} else {
				setSelectedMovement(value);
				parentOnChange(value);
			}
		};

		return { setQuery, setSelectedMovement, onChange };
	}, [searchState.query, creationLogic, parentOnChange]);

	// Compose all logic together
	return useMemo(() => {
		const { filteredMovements, hasExactMatch } = searchLogic.getSearchResults(searchState.query);

		return {
			filteredMovements,
			hasExactMatch,
			query: searchState.query,
			setQuery: selectionLogic.setQuery,
			selectedMovement: searchState.selectedMovement,
			onChange: selectionLogic.onChange,
			temporaryMovement: creationLogic.temporaryMovement,
			detectEquipmentInQuery,
		};
	}, [searchState, searchLogic, selectionLogic, creationLogic, detectEquipmentInQuery]);
}

function CreateSelectMovementCombobox(props: {
	onChange: (movement: MovementInfo | null) => void;
	initialQuery?: string;
}) {
	const { onChange: parentOnChange, initialQuery } = props;
	const inputRef = useRef<HTMLInputElement>(null);
	const {
		filteredMovements,
		hasExactMatch,
		selectedMovement,
		onChange,
		query,
		setQuery,
		temporaryMovement,
		detectEquipmentInQuery,
	} = useMovementCombobox(parentOnChange, initialQuery);
	const showCreateUserMovementButton = !hasExactMatch && query.length > 0;

	// Virtualization setup
	const [parentElement, setParentElement] = useState<HTMLDivElement | null>(null);
	const virtualizer = useVirtualizer({
		count: filteredMovements.length,
		getScrollElement: () => parentElement,
		estimateSize: () => MOVEMENT_OPTION_HEIGHT,
		overscan: 5, // Render 5 extra items above/below for smooth scrolling
	});
	const maxContainerHeight = MOVEMENT_OPTION_HEIGHT * MAX_VISIBLE_OPTIONS;

	// Auto-focus and open dropdown when initialQuery is provided
	useEffect(() => {
		if (initialQuery && inputRef.current) {
			// Small delay to ensure the component is fully mounted
			const timer = setTimeout(() => {
				inputRef.current?.focus();
			}, 16);
			return () => clearTimeout(timer);
		}
	}, [initialQuery]);

	// Auto-scroll to selected movement when dropdown opens
	// biome-ignore lint/correctness/useExhaustiveDependencies: Only trigger when dropdown opens/closes, not on every filter change
	useEffect(() => {
		if (!parentElement || !selectedMovement) {
			return;
		}

		const selectedIndex = filteredMovements.findIndex((m) => m.id === selectedMovement.id);

		if (selectedIndex !== -1) {
			virtualizer.scrollToIndex(selectedIndex, { align: 'center' });
		}
	}, [parentElement]);

	const handleQueryChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const newQuery = e.target.value;

			// If we have a prior selected movement, clear it as soon as the user starts typing
			// Note: Selected movement checkmark persists for one keystroke when editing.
			// This is a HeadlessUI rendering quirk - not worth the complexity to fix.
			if (selectedMovement) {
				onChange(null);
			}

			setQuery(newQuery);
		},
		[onChange, setQuery, selectedMovement],
	);

	const handleClear = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onChange(null);
			setQuery('');
		},
		[onChange, setQuery],
	);

	const handleClose = useCallback(() => {
		// Clear the query if nothing is selected
		if (!selectedMovement) {
			setQuery('');
		}
	}, [setQuery, selectedMovement]);

	const showClearButton = selectedMovement || query.length > 0;
	const detectedEquipmentKeywords = detectEquipmentInQuery(query);

	// Don't show warning if the query matches an existing movement name
	const isExistingMovementName = filteredMovements.some(
		(movement) => movement?.name?.toLowerCase() === query.toLowerCase(),
	);
	const showEquipmentWarning = detectedEquipmentKeywords.length > 0 && !isExistingMovementName;

	return (
		<div>
			<Combobox immediate={true} value={selectedMovement} onChange={onChange} onClose={handleClose}>
				<div className="relative">
					<ComboboxInput
						ref={inputRef}
						className={cn(
							'h-9 w-full rounded-md border bg-white px-3 text-gray-900 text-xs transition-colors placeholder:text-gray-500 focus:outline-none sm:h-10 sm:text-sm',
							showClearButton ? 'pe-16' : 'pe-8',
							// Equipment suggestion state (informational, not error)
							showEquipmentWarning &&
								!selectedMovement &&
								'border-blue-400 bg-blue-50/30 ring-2 ring-blue-400/20 hover:border-blue-500 focus:border-blue-500 focus:ring-blue-500/20',
							// Selected state
							selectedMovement && 'border-gray-500 ring-2 ring-gray-500/20',
							// Default state (when no warning and not selected)
							!showEquipmentWarning &&
								!selectedMovement &&
								'border-gray-300 hover:border-gray-400 focus:border-gray-500 focus:ring-2 focus:ring-gray-500/20',
						)}
						displayValue={(movement: MovementInfo | null) => movement?.name ?? query}
						onChange={handleQueryChange}
						placeholder="e.g., squat, press, curl..."
					/>

					{/* Clear button - show when typing or when movement is selected */}
					{showClearButton && (
						<button
							type="button"
							onClick={handleClear}
							className="group absolute inset-y-2 end-8 flex items-center justify-center rounded-md p-1.5 transition-colors hover:bg-gray-100"
							title="Clear"
						>
							<XIcon className="size-3.5 text-gray-400 transition-colors group-hover:text-gray-600" />
						</button>
					)}

					<ComboboxButton className="absolute inset-y-0 right-0 flex items-center justify-center p-2">
						<ChevronDownIcon className="size-4 text-muted-foreground" />
					</ComboboxButton>
				</div>

				<ComboboxOptions
					anchor="bottom"
					className="w-(--input-width) rounded-md border bg-popover p-1 text-popover-foreground shadow-md [--anchor-gap:--spacing(1)]"
					style={{
						maxHeight: '80vh',
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					{/* Non-virtualized: Create button (stays at top) */}
					{showCreateUserMovementButton && (
						<ComboboxOption
							value={temporaryMovement}
							className="group p-1"
							style={{ flexShrink: 0 }}
						>
							<button
								type="button"
								className="relative flex w-full cursor-pointer select-none items-center justify-between rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-left text-blue-900 text-xs transition-all duration-150 ease-in-out hover:border-blue-300 hover:bg-blue-100 active:scale-[0.98] group-data-focus:border-blue-400 group-data-focus:bg-blue-100 group-data-focus:shadow-sm sm:text-sm"
								title={`Create new movement "${query}"`}
							>
								<div className="flex min-w-0 flex-1 items-center gap-2.5">
									<UserIcon className="size-4 shrink-0 text-blue-700 opacity-80" />
									<span className="flex min-w-0 items-center gap-1.5">
										<span className="text-2xs text-blue-600 sm:text-xs">Create</span>
										<TruncatedText className="min-w-0 flex-1 truncate font-medium" text={query} />
									</span>
								</div>
								<kbd className="pointer-events-none ms-2 inline-flex h-5 shrink-0 select-none items-center gap-1 rounded border border-blue-200 bg-white px-1.5 font-medium font-mono text-blue-600 text-xs opacity-70">
									<span className="text-xs">⏎</span>
								</kbd>
							</button>
						</ComboboxOption>
					)}

					{/* Non-virtualized: Empty state */}
					{filteredMovements.length === 0 && (
						<div className="mt-1 border-gray-200 border-t py-10" style={{ flexShrink: 0 }}>
							<div className="p-2 font-medium text-gray-500 text-xs uppercase tracking-wider">
								<span className="flex items-center justify-center gap-1.5 text-gray-500 text-xs sm:text-sm">
									No results found
								</span>
							</div>
						</div>
					)}

					{/* VIRTUALIZED: Movement options */}
					{filteredMovements.length > 0 && (
						<div
							ref={setParentElement}
							style={{
								flex: '1 1 auto',
								minHeight: 0,
								maxHeight: `${maxContainerHeight}px`,
								overflowY: 'auto',
							}}
						>
							<div
								style={{
									height: `${virtualizer.getTotalSize()}px`,
									width: '100%',
									position: 'relative',
								}}
							>
								{virtualizer.getVirtualItems().map((virtualItem) => {
									const movement = filteredMovements[virtualItem.index];
									if (!movement) {
										return null;
									}
									return (
										<ComboboxOption
											key={movement.id}
											value={movement}
											className="group absolute flex w-full cursor-pointer items-center gap-2.5 px-3 text-gray-900 text-xs transition-colors data-focus:bg-gray-100 data-selected:bg-emerald-50 sm:text-sm"
											style={{
												position: 'absolute',
												top: 0,
												left: 0,
												width: '100%',
												height: `${virtualItem.size}px`,
												transform: `translateY(${virtualItem.start}px)`,
											}}
										>
											{movement.system === false && (
												<UserIcon className="size-4 text-gray-400 opacity-60" />
											)}
											<span className="flex-1 truncate">{movement.name}</span>
											<CheckIcon className="invisible size-4 shrink-0 text-emerald-600 group-data-selected:visible" />
										</ComboboxOption>
									);
								})}
							</div>
						</div>
					)}

					{/* Non-virtualized: Equipment detection hint (stays at bottom) */}
					{showEquipmentWarning && (
						<div
							className="mx-1 mt-2 mb-1 flex items-start gap-1.5 rounded-md border border-blue-200 bg-blue-50 p-2.5"
							style={{ flexShrink: 0 }}
						>
							<InfoIcon className="mt-0.5 size-3.5 flex-shrink-0 text-blue-600" />
							<div className="flex-1">
								<p className="text-2xs text-blue-700 sm:text-xs">
									<span className="font-medium">Tip:</span> Detected{' '}
									<span className="font-semibold">
										{detectedEquipmentKeywords.map((keyword, index) => (
											<span key={keyword}>
												{keyword}
												{index < detectedEquipmentKeywords.length - 1 ? ', ' : ''}
											</span>
										))}
									</span>
								</p>
								<p className="mt-1 text-2xs text-blue-600 sm:text-xs">
									Remove {detectedEquipmentKeywords.length > 1 ? 'these keywords' : 'this keyword'}{' '}
									and select {detectedEquipmentKeywords.length > 1 ? 'them' : 'it'} in "Primary
									Load" below instead
								</p>
							</div>
						</div>
					)}
				</ComboboxOptions>
			</Combobox>
		</div>
	);
}

export { CreateSelectMovementCombobox };
